import { forwardRef } from "react";
import {
  Heading as ChakraHeading,
  HeadingProps as ChakraHeadingProps,
} from "@chakra-ui/react";
import { type TextProps } from "../Text/Text.web";

const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(function Heading(
  props,
  ref
) {
  const { size = "md", as = "h2", ...rest } = props;
  const paintProps = paint(size);
  return (
    <ChakraHeading
      ref={ref}
      data-testid="heading"
      as={as}
      color="inherit"
      fontSize={paintProps.fontSize as ChakraHeadingProps["fontSize"]}
      fontWeight={paintProps.fontWeight}
      {...rest}
    />
  );
});

const HeadingPrimitive = forwardRef<HTMLHeadingElement, ChakraHeadingProps>(
  (props, ref) => {
    return <ChakraHeading ref={ref} {...props} />;
  }
);

HeadingPrimitive.displayName = "HeadingPrimitive";

type HeadingComponent = typeof Heading & {
  Primitive: typeof HeadingPrimitive;
};

const HeadingWithPrimitive = Heading as HeadingComponent;
HeadingWithPrimitive.Primitive = HeadingPrimitive;

export default HeadingWithPrimitive;

export type HeadingProps = TextProps & {
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl";
};

export function paint(size: HeadingProps["size"]): TextProps {
  // 1:1 mapping - heading size directly maps to fontSize token
  // sm = 14px, md = 16px, lg = 18px, xl = 20px, 2xl = 22px (1.375rem), etc.
  return {
    fontSize: size ?? "md",
    fontWeight: "bold",
  };
}
